import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabase';
import { apiService } from '../api';
import {
  portfolioService,
  Position,
  PortfolioSettings,
  PerformanceSnapshot,
  PortfolioSummary
} from '../services/portfolioService';

// Define the context type
interface PortfolioContextProps {
  // State
  settings: PortfolioSettings | null;
  positions: Position[];
  activePositions: Position[];
  performanceHistory: PerformanceSnapshot[];
  portfolioSummary: PortfolioSummary | null;
  isLoading: {
    settings: boolean;
    positions: boolean;
    performance: boolean;
    summary: boolean;
  };
  
  // Actions
  refreshPortfolio: () => Promise<void>;
  resetPortfolio: () => Promise<void>;
  updateSettings: (settings: Partial<PortfolioSettings>) => Promise<void>;
  createPosition: (ticker: string, shares: number, price: number, notes?: string) => Promise<Position | null>;
  closePosition: (positionId: string, exitPrice: number) => Promise<Position | null>;
  createPerformanceSnapshot: (portfolioValue: number) => Promise<void>;
}

// Create the context
const PortfolioContext = createContext<PortfolioContextProps | undefined>(undefined);

// Custom hook to use the portfolio context
export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  
  // State
  const [settings, setSettings] = useState<PortfolioSettings | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [activePositions, setActivePositions] = useState<Position[]>([]);
  const [performanceHistory, setPerformanceHistory] = useState<PerformanceSnapshot[]>([]);
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState({
    settings: false,
    positions: false,
    performance: false,
    summary: false,
  });
  
  // Load portfolio data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadPortfolioData();
    } else {
      // Reset state when user logs out
      setSettings(null);
      setPositions([]);
      setActivePositions([]);
      setPerformanceHistory([]);
      setPortfolioSummary(null);
    }
  }, [isAuthenticated, user]);
  
  // Refresh all portfolio data
  async function loadPortfolioData() {
    if (!isAuthenticated) return;
    
    // Load settings
    await loadSettings();
    
    // Load positions
    await loadPositions();
    
    // Load performance history
    await loadPerformanceHistory();
    
    // Load portfolio summary
    await loadPortfolioSummary();
  }
  
  // Load portfolio settings
  async function loadSettings() {
    setIsLoading(prev => ({ ...prev, settings: true }));
    try {
      const settings = await portfolioService.settings.getSettings();
      setSettings(settings);
    } catch (error) {
      console.error('Error loading portfolio settings:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, settings: false }));
    }
  }
  
  // Load positions
  async function loadPositions() {
    setIsLoading(prev => ({ ...prev, positions: true }));
    try {
      // Get all positions
      const allPositions = await portfolioService.positions.getAllPositions();
      setPositions(allPositions);
      
      // Get active positions
      const active = await portfolioService.positions.getActivePositions();
      setActivePositions(active);
    } catch (error) {
      console.error('Error loading positions:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, positions: false }));
    }
  }
  
  // Load performance history
  async function loadPerformanceHistory() {
    setIsLoading(prev => ({ ...prev, performance: true }));
    try {
      const history = await portfolioService.performance.getSnapshots();
      setPerformanceHistory(history);
    } catch (error) {
      console.error('Error loading performance history:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, performance: false }));
    }
  }
  
  // Load portfolio summary
  async function loadPortfolioSummary() {
    setIsLoading(prev => ({ ...prev, summary: true }));
    try {
      const summary = await portfolioService.getPortfolioSummary();
      setPortfolioSummary(summary);
    } catch (error) {
      console.error('Error loading portfolio summary:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, summary: false }));
    }
  }
  
  // Reset portfolio
  async function resetPortfolio() {
    if (!isAuthenticated) return;
    
    try {
      const updatedSettings = await portfolioService.settings.resetPortfolio();
      
      if (updatedSettings) {
        console.log('Portfolio has been reset');
        
        // Refresh data
        await loadPortfolioData();
      }
    } catch (error) {
      console.error('Error resetting portfolio:', error);
    }
  }
  
  // Update portfolio settings
  async function updateSettings(settingsUpdate: Partial<PortfolioSettings>) {
    if (!isAuthenticated) return;
    
    try {
      const updatedSettings = await portfolioService.settings.updateSettings(settingsUpdate);
      
      if (updatedSettings) {
        setSettings(updatedSettings);
        console.log('Portfolio settings updated');
        
        // Refresh summary
        await loadPortfolioSummary();
      }
    } catch (error) {
      console.error('Error updating portfolio settings:', error);
    }
  }
  
  // Create a new position (buy)
  async function createPosition(
    ticker: string,
    shares: number,
    price: number,
    notes?: string
  ): Promise<Position | null> {
    if (!isAuthenticated) return null;
    
    try {
      const position = await portfolioService.positions.createPosition(
        ticker,
        shares,
        price,
        notes
      );
      
      if (position) {
        console.log(`Successfully bought ${shares} shares of ${ticker}`);
        
        // Refresh data
        await loadPositions();
        await loadPortfolioSummary();
        
        return position;
      }
      return null;
    } catch (error) {
      console.error('Error creating position:', error);
      return null;
    }
  }
  
  // Close a position (sell)
  async function closePosition(
    positionId: string,
    exitPrice: number
  ): Promise<Position | null> {
    if (!isAuthenticated) return null;
    
    try {
      const position = await portfolioService.positions.closePosition(
        positionId,
        exitPrice
      );
      
      if (position) {
        console.log(`Successfully sold ${position.shares} shares of ${position.ticker}`);
        
        // Refresh data
        await loadPositions();
        await loadPortfolioSummary();
        
        return position;
      }
      return null;
    } catch (error) {
      console.error('Error closing position:', error);
      return null;
    }
  }
  
  // Create a performance snapshot
  async function createPerformanceSnapshot(portfolioValue: number) {
    if (!isAuthenticated) return;
    
    try {
      const snapshot = await portfolioService.performance.createSnapshot(portfolioValue);
      
      if (snapshot) {
        console.log('Performance snapshot created');
        
        // Refresh performance history
        await loadPerformanceHistory();
      }
    } catch (error) {
      console.error('Error creating performance snapshot:', error);
    }
  }
  
  // Refresh all portfolio data
  async function refreshPortfolio() {
    await loadPortfolioData();
  }
  
  const value = {
    // State
    settings,
    positions,
    activePositions,
    performanceHistory,
    portfolioSummary,
    isLoading,
    
    // Actions
    refreshPortfolio,
    resetPortfolio,
    updateSettings,
    createPosition,
    closePosition,
    createPerformanceSnapshot,
  };
  
  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
} 